<template>
	<div :class="{ 'player-root-portrait': portrait && obs, 'h-full w-full': multiview }">
		<div :class="{'player-container': !multiview, 'h-full': obs || multiview, 'player-container-portrait': portrait && obs}">
			<video class="player" :class="{ 'player-portrait': portrait && obs }" v-if="playbackPaused" :src="videoPauseImgUrl"></video>
			<div v-if="!buffered" class="player-loading-indicator absolute inset-0 z-10 flex items-center justify-center">
				<Spinner text="" classes="text-dark-8 my-3" spinner-color="var(--c-dark-8)" spinner-size="15px" />
			</div>
			<div v-if="playback" :class="{'player-controls-wrapper': !multiview}">
				<div :class="{ 'playing': playing }" class="player-controls">
					<div class="flex mt-5">
						<FormButton v-if="!playbackPaused" type="light" :isIcon="true" size="md" :class="['mr-2', 'btn_pause', 'px-2', { 'centered': isPreview }]"
							@click="stopPlayback()">
							<icon-base class="text-white" width="14" height="14" widthViewBox="20" heightViewBox="20">
								<icon-player-pause-on />
							</icon-base>
						</FormButton>
						<FormButton v-else type="light" :isIcon="true" size="md" :class="['btn_pause', 'px-0', { 'centered': isPreview }]" @click="initPlaybackSetup()">
							<icon-base class="text-white" width="16" height="16">
								<icon-player-pause-off />
							</icon-base>
						</FormButton>
						<template v-if="!isPreview">
							<FormButton v-if="!controls.volume" type="light" :isIcon="true" size="md" class="btn_mute px-0" @click="toggleVolume">
								<icon-base class="text-white" width="20" height="20" widthViewBox="20" heightViewBox="20">
									<icon-player-volume-off />
								</icon-base>
							</FormButton>
							<FormButton v-else type="light" :isIcon="true" size="md" class="btn_mute px-0" @click="toggleVolume">
								<icon-base class="text-white" width="20" height="20" widthViewBox="20" heightViewBox="20">
									<icon-player-volume-on />
								</icon-base>
							</FormButton>
						</template>
					</div>
					<FormButton type="light" :isIcon="true" size="md" class="btn_fullscreen px-0" @click="toggleFullscreen">
						<icon-base class="text-white" width="20" height="20" widthViewBox="20" heightViewBox="20">
							<icon-player-full-screen />
						</icon-base>
					</FormButton>
				</div>

			</div>
			<video :id="(refLink || refLink === 0) ? 'player-' + refLink : 'player'" :class="{ 'container-size': stream.type === 'ipcam' || stream.type === 'scheduled', 'player-portrait': portrait && obs }" />
		</div>
	</div>
</template>

<script>
import Spinner from "@/components/ui/Spinner.vue";
import MSEPlayer from '@flussonic/flussonic-mse-player';
import FormButton from '@/components/Atoms/FormButton.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconPlayerPauseOn from '@/components/icon/IconPlayerPauseOn.vue';
import IconPlayerPauseOff from '@/components/icon/IconPlayerPauseOff.vue';
import IconPlayerVolumeOn from '@/components/icon/IconPlayerVolumeOn.vue';
import IconPlayerVolumeOff from '@/components/icon/IconPlayerVolumeOff.vue';
import IconPlayerFullScreen from '@/components/icon/IconPlayerFullScreen.vue';

export default {
	name: 'StreamPlayer',
	components: {
		FormButton,
		Spinner,
		IconBase,
		IconPlayerPauseOn,
		IconPlayerPauseOff,
		IconPlayerVolumeOn,
		IconPlayerVolumeOff,
		IconPlayerFullScreen,
	},
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			}
		},
		mediaPulse: {
			type: Object,
			default() { return {}; }
		},
		streamUrl: {
			type: String,
			required: true
		},
		// Optional override for the stream key used when rewriting the playback
		// path (e.g. `${stream.key}_primary` / `_backup` for failover previews).
		// Defaults to stream.key so existing single-stream previews are unchanged.
		previewKey: {
			type: String,
			default: null,
			required: false
		},
		multiview: {
			type: Boolean,
			default: false
		},
		refLink: {
			type: Number,
			default: null,
			required: false
  		},
		obs: {
			type: Boolean,
			default: false,
			required: false
  		},
		isPreview: {
			type: Boolean,
			default: false,
			required: false
  		},
		portrait: {
			type: Boolean,
			default: false,
			required: false
		},
	},
	data() {
		return {
			bufferWaitTimeout: null,
			playing: false,
			playback: false,
			playbackUrl: null,
			playbackPaused: false,
			videoPauseImgUrl: null,
			buffered: true,
			controls: {
				volume: false,
				fullscreen: false
			},
			scopeAlive: true,
			flashDisabled: false,
			videoDOM: null,
			videoPlayer: null,
			wsErrorState: false
		};
	},
	mounted() {
		window.FlussonicMsePlayer = MSEPlayer;
		this.playbackUrl = this.streamUrl;

		const hostId = this.mediaPulse && this.mediaPulse.hostId;
		if (hostId) {
			const staticPrefix = this.mediaPulse.staticPrefix;
			const anc = document.createElement('a');
			anc.href = this.playbackUrl;

			if (/\w+-\d+/gi.test(hostId) || this.stream.pullUrl) {
				anc.hostname = `${hostId}.castr.io`;
				const keyToReplace = this.previewKey || this.stream.key;
				anc.pathname = anc.pathname.replace(keyToReplace, staticPrefix ? `static/${this.mediaPulse.name}` : this.mediaPulse.name);
			}

			this.playbackUrl = anc.href;
		}

		if (this.obs) {
			const hostId = this.mediaPulse && this.mediaPulse.hostId;
			console.log('mediaflow player init test', hostId)
			this.playbackUrl = `wss://${hostId}.castr.io/${this.stream.key}/mse_ld`;
		}

		// `fake` is the placeholder host callers put in the URL for the pulse's
		// hostId to replace; if it's still there the serving edge is unknown.
		if (this.hasPlaceholderHost(this.playbackUrl)) {
			console.warn('StreamPlayer: serving host not resolved yet, skipping playback', this.playbackUrl);
			return;
		}

		this.initPlaybackSetup();
	},
	unmounted() {
		this.scopeAlive = false;
		// window.alert('player comp destroyed')
		if (this.videoPlayer) this.stopPlayback();
		// this.videoPlayer.stop();
	},
	methods: {
		hasPlaceholderHost(url) {
			try {
				return new URL(url).hostname === 'fake';
			} catch (e) {
				return false;
			}
		},
		initPlaybackSetup() {
			if (!this.scopeAlive) return;

			this.playbackPaused = false;

			const element = document.getElementById((this.refLink || this.refLink === 0)  ? 'player-' + this.refLink : 'player');

			if (!element) return;

			this.videoDOM = element;
			this.videoDOM.volume = 0;

			const playerConfig = {
				bufferMode: 'sequence',
				statsSendEnable: false,
				onProgress: () => {
					this.playing = true;
				},
				onMediaInfo: () => {
					this.playback = true;
				},
				onError: () => {
					console.error('ws: error')
					if (this.wsErrorState) {
						console.error('ws: error state bypass')
						return
					}
					this.wsErrorState = true
					if (!this.scopeAlive) return;

					this.stopPlayback(() => {
						this.wsErrorState = false
						if (!this.scopeAlive) return;

						window.player.play();
						if (window.player.setBufferMode) { window.player.setBufferMode('segments'); }
					});
				}
			};

			// eslint-disable-next-line no-undef
			console.log('init mse player', this.playbackUrl)
			const playerInstance = new FlussonicMsePlayer(
				element,
				this.playbackUrl,
				playerConfig
			);

			this.buffered = false;
			playerInstance.play();

			const bufferWaitTimeout = setTimeout(() => {
				this.stopPlayback(() => {
					this.initPlaybackSetup();
				});
			}, 15 * 1000);

			window.player = this.videoPlayer = playerInstance;

			// DOM events
			if (element) {
				element.onplaying = () => {
					this.buffered = true;
					clearTimeout(bufferWaitTimeout);
				};
			}
		},
		stopPlayback(cb) {
			// const { player } = window;
			const player = this.videoPlayer;
			this.playing = false;
			if (this.controls.volume) {
				this.toggleVolume();
			}
			const regex = /(\/mse_).*/s;
			const str = this.playbackUrl;
			const subst = ``;
			const result = str.replace(regex, subst);
			this.videoPauseImgUrl = 'https' + result.substring(3) + '/preview.mp4';
			try {
				player.pause();
			} catch (e) { }

			setTimeout(async () => {
				player.stop();
				this.playbackPaused = true;

				if (player.stopPromise) {
					await player.stopPromise;
				}
				if (cb) {
					cb();
				}
			}, 500);
		},
		toggleVolume() {
			const oldVolume = this.videoDOM.volume;
			this.videoDOM.volume = oldVolume === 1 ? 0 : 1;

			this.controls.volume = this.videoDOM.volume === 1;
		},
		toggleFullscreen() {
			const elem = this.videoDOM;
			if (elem.requestFullscreen) {
				elem.requestFullscreen();
			} else if (elem.mozRequestFullScreen) {
				elem.mozRequestFullScreen();
			} else if (elem.webkitRequestFullscreen) {
				elem.webkitRequestFullscreen();
			}
		}
	}
};
</script>

<style scoped>
.btn_pause,
.btn_mute,
.btn_fullscreen {
	@apply !rounded-full !bg-black/40 backdrop-blur-sm hover:!bg-black/55;
}

.btn_pause {
	position: absolute;
	bottom: 10px;
	left: calc(50% - 48px);
	z-index: 11;
}

.btn_pause.centered {
	left: calc(50% - 24px);
}

.btn_mute {
	position: absolute;
	bottom: 10px;
	left: calc(50% + 12px);
	z-index: 11;
}

.btn_fullscreen {
	position: absolute;
	bottom: 10px;
	right: 10px;
	z-index: 11;
}

.player {
	position: absolute;
	top: 0;
	left: 0;
}

.player-root-portrait {
	position: absolute;
	inset: 0;
}

.player-container-portrait {
	width: 100%;
	height: 100%;
}

.player-portrait {
	width: 100% !important;
	height: 100% !important;
	object-fit: contain;
}

</style>